/**
 * CaseDetail — one conversation read LIVE from the back office via
 * `member.cases.detail` (038 US1–US3; 040 US1–US2 reply + attachment upload).
 * Route-on-read; no local copy (FR-006/9).
 *
 * Extracted from the former `MyCaseDetail` route (retired with `/my-cases/:id`
 * in the same 030 US3 change) so the unified "Viestit" detail at `/messages/:id`
 * reuses the exact thread/reply/attachment behaviour. The `notFound` slot is the
 * one surface-specific input: Viestit lands the member back on the list (IDOR
 * non-disclosure, FR-003) via a `<Navigate>`.
 *
 * ── 055 prototype convergence ───────────────────────────────────────────────
 *
 * The header is now subject + state pill (the back control lives in the route
 * shell, `MessageDetail`), matching the prototype;
 * the former Tila/Luokka/Luotu `<dl>` is GONE (verified against the prototype
 * source — it has no such block). The thread, attachment chips, and the
 * "Yhteydenottosi" summary moved into their own modules, both because this file
 * was already at the project's working length ceiling and because the pill and
 * the thread are shared or independently testable concerns.
 *
 * Opening the conversation now marks the WHOLE thread read (055 FR-004); the
 * per-message "mark as read" control is retired. See `markReadOnceRef` below for
 * why that call is deliberately silent on failure.
 */

import {
  type JSX,
  type ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useTransition,
} from 'react';

import { AnalyticsRouteReady } from '../analytics/AnalyticsCaptureProvider.js';
import { useLanguage } from '../i18n/useLanguage.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { PortsContext } from '../providers/PortsContext.js';
import { AttachmentChips } from './AttachmentChips.js';
import { ContactSummary, shouldShowContactSummary } from './ContactSummary.js';
import { ConversationStatusPill } from './ConversationStatusPill.js';
import { MessageThread } from './MessageThread.js';
import { ReplyComposer } from './ReplyComposer.js';
import { useCaseDetail } from './useCaseDetail.js';
import { invalidateCasesCache } from './useCases.js';

const noteClasses = 'text-sm text-muted-foreground';
const headingClasses = 'text-xl sm:text-2xl font-bold leading-snug text-foreground break-words';
const sectionHeadingClasses = 'text-lg font-semibold text-foreground mt-8 mb-3';

/**
 * The suspending real-case detail body. Renders inside a
 * `PortErrorBoundary(memberCases)` + `Suspense` the caller provides.
 *
 * `notFound` is what renders when the read resolves not-found / not-owned: the
 * sole live consumer (the Viestit detail) passes a redirect to the `/messages`
 * list (FR-003 IDOR non-disclosure).
 */
export function CaseDetailBody({
  caseId,
  notFound,
}: {
  readonly caseId: string;
  readonly notFound: ReactNode;
}): JSX.Element {
  const t = useTranslate();
  const { active } = useLanguage();
  // 040 US1 — route-on-read after a successful reply/upload: bumping this token
  // re-reads the detail live (never a local append, P7).
  const [reloadToken, setReloadToken] = useState(0);
  const [, startReloadTransition] = useTransition();
  const view = useCaseDetail(caseId, active, reloadToken);
  const ports = useContext(PortsContext);
  const addMessage = ports?.memberCases?.addMessage.bind(ports.memberCases);
  const uploadAttachment = ports?.memberCases?.uploadAttachment.bind(ports.memberCases);
  const casesPort = ports?.memberCases;
  const markConversationRead = casesPort?.markConversationRead.bind(casesPort);

  // 053 US1 — closed-state accessibility on transition. When a reply/upload reload
  // reveals the conversation NOW closed (composer + upload removed), an AT user who
  // had focus on the removed control would be left on <body>. We convey the change
  // with ONE mechanism per scenario (FR-001):
  //   - if the element focused when the reload was triggered is REMOVED by the
  //     close, move focus to the closed-state note (`closedNoteRef`, tabIndex=-1)
  //     — the focus read re-anchors AND announces it;
  //   - otherwise (focus was elsewhere) inject the note text into a pre-existing,
  //     empty polite live region (`announceClosed`) WITHOUT stealing focus.
  // Detection is on the open/unknown→closed TRANSITION only (prev-value ref keyed
  // by caseId), so the already-closed steady state and open→open reloads do
  // nothing (FR-002). The `useTransition`-wrapped reload keeps this component
  // MOUNTED across the refetch, so the ref survives and focus is not dropped to
  // the Suspense fallback before the note exists.
  const closedNoteRef = useRef<HTMLParagraphElement>(null);
  const focusedAtReloadRef = useRef<Element | null>(null);
  const prevClosedRef = useRef<{ caseId: string; closed: boolean } | null>(null);
  const [announceClosed, setAnnounceClosed] = useState('');
  const reloadDetail = (): void => {
    // Capture what had focus BEFORE the transition commits (the composer/upload
    // control the member just used); after commit a removed element is gone.
    focusedAtReloadRef.current = document.activeElement;
    startReloadTransition(() => setReloadToken((n) => n + 1));
  };

  const isOk = view.status === 'ok';
  const closedAt = view.status === 'ok' ? view.case.closedAt : undefined;
  const closedReplyNote = t('myCases.detail.closedReplyNote');

  // 055 FR-004 — mark the WHOLE conversation read on open. One call, one audit
  // event; the per-message fan-out to the IS happens server-side (the IS exposes
  // no conversation-level read endpoint).
  //
  // The ref is keyed on caseId so this fires ONCE per conversation opened, not
  // on every render, not on the post-reply re-read, and not on a locale switch —
  // each of those re-runs this effect with the same conversation.
  //
  // Deliberately SILENT on every outcome (FR-005): a failure must not block
  // reading, must not surface an error, and must not show a false confirmation.
  // The conversation simply stays unread, which is honest — the unread signal
  // keeps reflecting the back office. The ref is NOT reset on failure, so the
  // retry happens the next time the conversation is opened (a remount or a
  // switch to another caseId), not within this mounted instance.
  //
  // That "no in-session retry" is a DECISION, not an omission (owner ruling
  // 2026-09-30, D-232(g)): a stale unread flag is mildly annoying but never
  // wrong, whereas a background retry adds moving parts and can still fail.
  // Surfacing the failure was rejected too — it would put a technical error in
  // front of someone who just wanted to read their message and can do nothing
  // about it.
  //
  // On `ok` the cached Viestit list page is evicted (052 `invalidateCasesCache`)
  // so returning to the list is a fresh route-on-read and the row no longer
  // shows unread (055 US2 AS6). `unavailable` and a rejection evict too: a
  // partial failure may already have committed marks upstream, and eviction
  // only forces a fresh read. `nothing-to-mark` and `not-found` changed nothing
  // upstream, so the cached page is still honest. Eviction does not re-render a
  // list that is already mounted, so a return BEFORE the mark settles can still
  // show the row as unread until the next list read — an accepted window
  // (contracts/member-cases-read-and-reply.md §1).
  const markReadOnceRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isOk || markConversationRead === undefined) {
      return;
    }
    if (markReadOnceRef.current === caseId) {
      return;
    }
    markReadOnceRef.current = caseId;
    void markConversationRead(caseId)
      .then((outcome) => {
        if ((outcome === 'ok' || outcome === 'unavailable') && casesPort !== undefined) {
          invalidateCasesCache(casesPort);
        }
      })
      .catch(() => {
        // Intentionally swallowed — see above. Nothing is shown to the member.
        if (casesPort !== undefined) {
          invalidateCasesCache(casesPort);
        }
      });
  }, [caseId, isOk, markConversationRead, casesPort]);

  // useLayoutEffect (not useEffect): the focus-move branch must re-anchor focus
  // synchronously after the DOM mutation and BEFORE paint, so no frame is ever
  // painted (nor exposed to AT) with focus stranded on <body> after the close
  // removes the composer (FR-003/INV-2).
  useLayoutEffect(() => {
    if (!isOk) {
      prevClosedRef.current = null;
      return;
    }
    const closed = closedAt != null;
    const prev = prevClosedRef.current;
    const isTransition = prev !== null && prev.caseId === caseId && !prev.closed && closed;
    prevClosedRef.current = { caseId, closed };
    if (!isTransition) {
      // Not a transition (steady state, open→open, or a case switch): clear any
      // prior announcement so a later close re-announces cleanly, and drop any
      // captured focus target so a stale detached element (from an earlier
      // open→open reply reload) can never drive a spurious focus steal on a
      // later close not initiated by reloadDetail (e.g. a locale-switch re-read).
      setAnnounceClosed('');
      focusedAtReloadRef.current = null;
      return;
    }
    const focusedEl = focusedAtReloadRef.current;
    const focusWasRemoved = focusedEl instanceof HTMLElement && !document.body.contains(focusedEl);
    if (focusWasRemoved) {
      closedNoteRef.current?.focus();
    } else {
      setAnnounceClosed(closedReplyNote);
    }
    focusedAtReloadRef.current = null;
  }, [caseId, isOk, closedAt, closedReplyNote]);

  if (view.status === 'unavailable') {
    return <p className={noteClasses}>{t('myCases.detail.unavailable')}</p>;
  }
  if (view.status === 'not-found') {
    return <>{notFound}</>;
  }

  const c = view.case;
  const showSummary = shouldShowContactSummary(c.description, c.messages);

  return (
    <>
      <AnalyticsRouteReady pageKey={{ kind: 'memberRoute', route: 'member-case-detail' }} />
      {/* 053 US1 — persistent polite live region: present in BOTH open and closed
          states (rendered empty), so when the conversation transitions to closed
          with focus elsewhere, injecting the note text here is reliably announced
          (a region mounted together with its text is not). The focus-move scenario
          does not use this region (it focuses the note itself). */}
      <p aria-live="polite" aria-atomic="true" className="sr-only">
        {announceClosed}
      </p>

      {/* 055: header is subject + state pill only. The prototype carries no
          Tila/Luokka/Luotu block, and the pill already states the handling
          state; the creation date now lives on the summary block below, and is
          intentionally absent when that block is suppressed (fund-initiated or no
          description — FR-003). */}
      <div className="mb-5">
        <h2 className={headingClasses}>
          {c.title !== null && c.title.trim().length > 0 ? c.title : t('myCases.untitled')}
        </h2>
        <div className="mt-1.5">
          <ConversationStatusPill status={c.status} closedAt={c.closedAt} size="header" />
        </div>
      </div>

      {/* 055 FR-003 — suppressed entirely on a fund-initiated conversation. */}
      {showSummary && c.description !== null ? (
        <div className="mb-3">
          <ContactSummary description={c.description} createdAt={c.createdAt} active={active} />
        </div>
      ) : null}

      {/* The thread keeps its own heading so AT users can jump to it and it
          does not read as part of the "Yhteydenottosi" section. Visually hidden:
          the prototype layout shows no heading here. */}
      <h3 className="sr-only">{t('myCases.detail.messagesHeading')}</h3>
      <MessageThread messages={c.messages} active={active} />

      {/* 055 R-003 / D-229(a): conversation-level attachments sit BELOW THE
          THREAD and above the reply, matching the prototype's reading order. */}
      {/* 055 (owner decision 2026-09-30): the section appears only when there is
          something in it. Most conversations have no attachments, and a heading
          whose only content is "Ei liitteitä." is pure scroll between the thread
          and the reply box on a phone. The prototype has no standalone
          attachments section at all — it shows files inside the messages, which
          Oma+ cannot (D-229(a) / O-028). */}
      {c.attachments.length > 0 ? (
        <>
          <h3 className={sectionHeadingClasses}>{t('myCases.detail.attachmentsHeading')}</h3>
          <AttachmentChips caseId={c.id} attachments={c.attachments} />
        </>
      ) : null}

      {/* 050 US5: reply is SUPPRESSED for a closed conversation (keyed off
          closedAt only). An open/unknown (nullish) conversation keeps the
          composer. When closed, show an honest note instead of the composer. */}
      {c.closedAt != null ? (
        // 053 US1: tabIndex=-1 makes the note a programmatic focus target for the
        // "focus was on a removed control" scenario (the focus read announces it);
        // it is in the page language (Finnish page → Finnish note), so it inherits
        // the document `lang` and needs no `lang` part attribute.
        <p ref={closedNoteRef} tabIndex={-1} className={`${noteClasses} mt-4`}>
          {closedReplyNote}
        </p>
      ) : addMessage !== undefined ? (
        // 055: attaching and sending are ONE action now — the former separate
        // AttachmentUpload control is folded into the composer. Suppressing the
        // composer therefore suppresses uploading too, which is the same
        // symmetry 050 US5 required, now structural rather than two conditions.
        <ReplyComposer
          caseId={c.id}
          addMessage={addMessage}
          {...(uploadAttachment !== undefined ? { uploadAttachment } : {})}
          onReplyAdded={reloadDetail}
        />
      ) : null}
    </>
  );
}
